import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  FormGroup,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import Services from 'src/services/Services';
import { tokenService } from 'src/services';
import resim from '../../assets/images/logo-form.png';
import { toast } from 'react-toastify';
import { useLocalization } from 'src/context/LocalizationContext';
import { useNavigate } from 'react-router-dom';

const SurveySaveSolutions = ({ formId }) => {
  const { language } = useLocalization();
  const [surveyList, setSurveyList] = useState(null);
  const [answers, setAnswers] = useState({});
  const [mlToken, setMlToken] = useState('');
  const [isLogged, setIsLogged] = useState(false);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const stripHtmlTags = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  const sanitizeHTML = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.innerText || doc.body.textContent;
  };

  // 🔥 Survey çekme – Services.GetSurvey (ML token ile)
  const getSurvey = async () => {
    setLoading(true);
    try {
      const res = await Services.GetSurvey(formId);

      if (res.data?.result?.[0]?.status === '2') {
        toast.warn('Bu formun içeriğine erişim izniniz yok.');
        navigate('/dashboard');
        return;
      }

      if (res.data?.status) {
        setSurveyList(res.data.result[0]);
      } else {
        console.log('GetSurvey response-error:', res);
      }
    } catch (error) {
      console.log('GetSurvey error:', error.message);
    } finally {
      setLoading(false);
    }
  };

  // Init: Login olsa da olmasa da ML token al ve onu kullan
  useEffect(() => {
    const init = async () => {
      const user = tokenService.getUser();
      setIsLogged(!!user);
      await getSurvey();
    };

    init();
  }, []);

  const handleAnswerChange = (questionIndex, answer, isChecked = null) => {
    setAnswers((prevAnswers) => {
      const updatedAnswers = { ...prevAnswers };

      if (isChecked !== null) {
        // Checkbox için
        if (isChecked) {
          if (!Array.isArray(updatedAnswers[questionIndex])) {
            updatedAnswers[questionIndex] = [];
          }
          updatedAnswers[questionIndex].push(answer);
        } else {
          updatedAnswers[questionIndex] = (
            updatedAnswers[questionIndex] || []
          ).filter((selectedAnswer) => selectedAnswer !== answer);
        }
      } else {
        // Diğer soru türleri için
        updatedAnswers[questionIndex] = answer;
      }

      return updatedAnswers;
    });
  };

  const isFormValid = () => {
    if (!surveyList?.questions || surveyList.questions.length === 0)
      return false;

    return surveyList.questions.every((question, index) => {
      const answer = answers[index];

      if (question.type === 'short_answer') {
        return answer && answer.trim() !== '';
      }
      if (question.type === 'multi_choice' && !question.canSelectOneMore) {
        return !!answer;
      }
      if (question.type === 'multi_choice' && question.canSelectOneMore) {
        return Array.isArray(answer) && answer.length > 0;
      }
      if (question.type === 'rating') {
        return !!answer;
      }
      return true;
    });
  };

  // 🔥 Save – Services.SaveSurvey (ML token ile)
  const handleSave = async () => {
    if (!mlToken) {
      toast.error('Token alınamadı. Lütfen sayfayı yenileyin.');
      return;
    }
    if (!surveyList?.questions?.length) {
      toast.error('Form yüklenemedi.');
      return;
    }

    const user = tokenService.getUser();

    const payload = {
      user: {
        userID: isLogged ? user?.userID : false,
        name: isLogged ? user?.name : 'Anonim',
        lastname: isLogged ? user?.lastname : 'Lastname Anonim',
      },
      surveyID: formId,
      response: surveyList.questions.map((question, index) => ({
        question: sanitizeHTML(question.label),
        type: question.type,
        canSelectOneMore: question.canSelectOneMore,
        options: question.options || false,
        response: answers[index] || null,
      })),
    };

    try {
      const res = await Services.SaveSurvey(mlToken, payload);

      if (res.data?.status) {
        toast.success('Cevaplar başarıyla kaydedildi!');
        setAnswers({});
        setTimeout(() => {
          window.close();
        }, 2500);
      } else {
        console.log('SaveSurvey response-error:', res);
        toast.error('Cevaplar kaydedilemedi.');
      }
    } catch (error) {
      console.error('SaveSurvey error:', error.message);
      toast.error('Cevap gönderilirken hata oluştu.');
    }
  };

  return (
    <Box>
      <Stack
        justifyContent="left"
        flexDirection="row"
        sx={{
          boxShadow: '2px 1px 4px 0px rgba(0, 0, 0, 0.1)',
          background: 'white',
          padding: '15px',
          marginBottom: '20px',
        }}
      >
        <Box sx={{ marginLeft: '50px' }}>
          <img height="80" width="120" src={resim} alt="Easypoint Logo" />
        </Box>
      </Stack>

      <Container
        maxWidth="lg"
        sx={{
          padding: 3,
          backgroundColor: 'white',
          borderRadius: 3,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack sx={{ borderBottom: '1px solid #E5E5E5', marginBottom: '45px' }}>
          <Typography variant="h4" gutterBottom textAlign="center">
            {surveyList?.title || language.commons.surveys.survey_loading}
          </Typography>
        </Stack>

        <Stack sx={{ paddingX: 5 }}>
          <Grid container spacing={5}>
            {surveyList?.questions && surveyList.questions.length > 0 ? (
              surveyList.questions.map((question, index) => {
                const minValue = question.options?.[0]?.minValue;
                const maxValue = question.options?.[0]?.maxValue;

                const ratings =
                  typeof minValue === 'number' && typeof maxValue === 'number'
                    ? Array.from(
                        { length: maxValue - minValue + 1 },
                        (_, i) => minValue + i
                      )
                    : [];

                return (
                  <Grid item xs={12} md={6} key={index}>
                    <Box sx={{ marginBottom: 3, flex: 1 }}>
                      <Stack sx={{ paddingBottom: '15px' }}>
                        <Typography variant="p">
                          {index + 1}. {stripHtmlTags(question.label)}
                        </Typography>

                        {question.type === 'rating' &&
                          question.options?.[0] && (
                            <Typography
                              variant="span"
                              sx={{ ml: 1, color: 'text.secondary' }}
                            >
                              ({ratings[0]} : {question.options[0].minContent} /{' '}
                              {ratings[ratings.length - 1]} :{' '}
                              {question.options[0].maxContent})
                            </Typography>
                          )}
                      </Stack>

                      <Stack sx={{ paddingLeft: '10px', paddingTop: '20px' }}>
                        {question.type === 'short_answer' && (
                          <TextField
                            label={language.commons.labels.answer_pls}
                            variant="outlined"
                            fullWidth
                            value={answers[index] || ''}
                            onChange={(e) =>
                              handleAnswerChange(index, e.target.value)
                            }
                          />
                        )}

                        {question.type === 'multi_choice' &&
                          !question.canSelectOneMore && (
                            <RadioGroup
                              value={answers[index] || ''}
                              onChange={(e) =>
                                handleAnswerChange(index, e.target.value)
                              }
                            >
                              {question.options.map((option, i) => (
                                <FormControlLabel
                                  key={i}
                                  value={option}
                                  control={<Radio />}
                                  label={option}
                                />
                              ))}
                            </RadioGroup>
                          )}

                        {question.type === 'multi_choice' &&
                          question.canSelectOneMore && (
                            <FormGroup sx={{ flexDirection: 'column' }}>
                              {question.options.map((option, optionIndex) => (
                                <FormControlLabel
                                  key={optionIndex}
                                  control={
                                    <Checkbox
                                      onChange={(e) =>
                                        handleAnswerChange(
                                          index,
                                          option,
                                          e.target.checked
                                        )
                                      }
                                      color="primary"
                                    />
                                  }
                                  label={
                                    <Typography
                                      variant="body1"
                                      sx={{ color: 'text.secondary' }}
                                    >
                                      {option}
                                    </Typography>
                                  }
                                  value={option}
                                  name={`question_${index}_${optionIndex}`}
                                />
                              ))}
                            </FormGroup>
                          )}

                        {question.type === 'rating' && (
                          <Stack spacing={2}>
                            <Stack
                              direction="row"
                              spacing={2}
                              flexWrap="wrap"
                              alignItems="center"
                            >
                              {ratings.map((rating, ratingIndex) => (
                                <React.Fragment key={ratingIndex}>
                                  <Stack alignItems="center">
                                    <Button
                                      variant="outlined"
                                      onClick={() =>
                                        handleAnswerChange(index, rating)
                                      }
                                      sx={{
                                        minWidth: '30px',
                                        minHeight: '30px',
                                        bgcolor:
                                          answers[index] === rating
                                            ? '#2065D1'
                                            : 'transparent',
                                        color:
                                          answers[index] === rating
                                            ? 'white'
                                            : '#000',
                                        padding: '2px 2px',
                                        transition:
                                          'background-color 0.3s, transform 0.2s',
                                        '&:hover': {
                                          bgcolor: '#2065D1',
                                          transform: 'scale(1.05)',
                                        },
                                      }}
                                    >
                                      {rating}
                                    </Button>
                                  </Stack>
                                </React.Fragment>
                              ))}
                            </Stack>
                          </Stack>
                        )}
                      </Stack>
                    </Box>
                  </Grid>
                );
              })
            ) : loading ? (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{ width: '100%', paddingY: 6 }}
              >
                <CircularProgress />
              </Stack>
            ) : (
              <Typography textAlign="center">
                {language.commons.surveys.no_question}
              </Typography>
            )}
          </Grid>
        </Stack>

        <Stack alignItems="center">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={!isFormValid()}
            sx={{ marginTop: 3, width: 150 }}
          >
            {language.commons.send}
          </Button>
        </Stack>
      </Container>
    </Box>
  );
};

export default SurveySaveSolutions;
